class ShopTheLook extends HTMLElement {
  constructor() {
    super();
    
    this.track = this.querySelector('.shop-the-look__track');
    this.items = this.querySelectorAll('.shop-the-look__item');
    this.prevBtn = this.querySelector('.shop-the-look__nav-button--prev');
    this.nextBtn = this.querySelector('.shop-the-look__nav-button--next');
    this.addToCartButtons = this.querySelectorAll('.shop-the-look__add-to-cart');
    
    this.currentIndex = 0;
    this.itemsPerView = this.getItemsPerView();

    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.handleReposition = this.handleReposition.bind(this);

    this._activePopup = null;
    this._activeTriggerButton = null;
    
    this.init();
  }

  init() {
    if (!this.track || this.items.length === 0) return;
    
    // Setup navigation
    this.updateNavigation();
    
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate('prev'));
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate('next'));
    }
    
    // Setup add to cart buttons
    this.addToCartButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openVariantPopup(button);
      });
    });
    
    // Handle window resize
    window.addEventListener('resize', () => {
      const newItemsPerView = this.getItemsPerView();
      if (newItemsPerView !== this.itemsPerView) {
        this.itemsPerView = newItemsPerView;
        this.currentIndex = 0;
        this.updateCarousel();
      }
    });
  }

  getItemsPerView() {
    const width = window.innerWidth;
    if (width < 750) return 2;
    if (width < 990) return 3;
    return 5;
  }

  navigate(direction) {
    const maxIndex = Math.max(0, this.items.length - this.itemsPerView);
    
    if (direction === 'prev') {
      this.currentIndex = Math.max(0, this.currentIndex - 1);
    } else {
      this.currentIndex = Math.min(maxIndex, this.currentIndex + 1);
    }
    
    this.updateCarousel();
  }

  updateCarousel() {
    const itemWidth = this.items[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
    const offset = -(this.currentIndex * (itemWidth + gap));
    
    this.track.style.transform = `translateX(${offset}px)`;
    this.updateNavigation();
  }

  updateNavigation() {
    const maxIndex = Math.max(0, this.items.length - this.itemsPerView);
    
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= maxIndex;
    }
  }

  openVariantPopup(button) {
    const productHandle = button.dataset.productHandle;
    
    if (!productHandle) return;
    
    // Create or get popup
    let popup = document.querySelector('.shop-the-look-popup');
    if (!popup) {
      popup = document.createElement('div');
      popup.className = 'shop-the-look-popup';
      popup.innerHTML = `
        <div class="shop-the-look-popup__content shop-the-look-popup__content--tooltip">
          <div class="shop-the-look-popup__body">
            <button class="shop-the-look-popup__close" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            <div class="shop-the-look-popup__loading">Loading...</div>
          </div>
        </div>
      `;
      document.body.appendChild(popup);

      // Delegate close button clicks (button gets re-rendered)
      popup.addEventListener('click', (e) => {
        if (e.target?.closest?.('.shop-the-look-popup__close')) {
          this.closePopup(popup);
        }
      });
    }
    
    popup.classList.add('active');
    popup.setAttribute('data-state', 'select');

    this.setPopupContentMode(popup, 'tooltip');

    this._activePopup = popup;
    this._activeTriggerButton = button;

    // Close when clicking anywhere outside tooltip
    document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
    window.addEventListener('scroll', this.handleReposition, true);
    window.addEventListener('resize', this.handleReposition, true);
    
    // Position the tooltip near the button
    this.positionTooltip(popup, button);
    
    this.loadProductData(productHandle, popup, button);
  }

  positionTooltip(popup, button) {
    const content = popup.querySelector('.shop-the-look-popup__content');
    const rect = button.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    
    // Position below the button
    let top = rect.bottom + scrollY + 8;
    let left = rect.left + scrollX + (rect.width / 2);
    
    // Adjust if would go off screen
    const viewportWidth = window.innerWidth;
    const estimatedWidth = 250; // Approximate popup width
    
    // Center on button, but keep within viewport
    left = Math.max(10, Math.min(left - (estimatedWidth / 2), viewportWidth - estimatedWidth - 10));
    
    content.style.top = top + 'px';
    content.style.left = left + 'px';
  }

  closePopup(popup) {
    popup.classList.remove('active');

    this.setPopupContentMode(popup, 'tooltip');

    document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
    window.removeEventListener('scroll', this.handleReposition, true);
    window.removeEventListener('resize', this.handleReposition, true);

    this._activePopup = null;
    this._activeTriggerButton = null;

    setTimeout(() => {
      popup.setAttribute('data-state', 'select');
    }, 300);
  }

  setPopupContentMode(popup, mode) {
    const content = popup?.querySelector?.('.shop-the-look-popup__content');
    if (!content) return;

    content.classList.toggle('shop-the-look-popup__content--tooltip', mode === 'tooltip');
    content.classList.toggle('shop-the-look-popup__content--modal', mode === 'modal');

    // Tooltip positioning sets inline top/left; clear them for the centered modal.
    if (mode === 'modal') {
      content.style.top = '';
      content.style.left = '';
      content.style.right = '';
      content.style.bottom = '';
      content.style.transform = '';
    }
  }

  handleDocumentPointerDown(e) {
    if (!this._activePopup || !this._activePopup.classList.contains('active')) return;

    const content = this._activePopup.querySelector('.shop-the-look-popup__content');
    if (content && content.contains(e.target)) return;
    if (this._activeTriggerButton && this._activeTriggerButton.contains(e.target)) return;

    this.closePopup(this._activePopup);
  }

  handleReposition() {
    if (!this._activePopup || !this._activeTriggerButton) return;
    if (this._activePopup.getAttribute('data-state') !== 'select') return;

    this.positionTooltip(this._activePopup, this._activeTriggerButton);
  }

  async loadProductData(handle, popup, triggerButton) {
    const body = popup.querySelector('.shop-the-look-popup__body');
    
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');
      
      const product = await response.json();
      
      console.log('Loaded product:', product);
      
      // Also fetch related products
      const relatedProducts = await this.fetchRelatedProducts(product);
      
      console.log('Related products for confirmation:', relatedProducts);
      
      this.renderProductPopup(product, relatedProducts, body, popup);

      // Reposition after content renders (dimensions change)
      if (triggerButton) {
        this.positionTooltip(popup, triggerButton);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      body.innerHTML = `
        <button class="shop-the-look-popup__close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
        <div class="shop-the-look-popup__error">Error loading product. Please try again.</div>
      `;
    }
  }

  async fetchRelatedProducts(product) {
    try {
      // Get all products from the main shop-the-look carousel on current page
      const mainCarousel = document.querySelector('shop-the-look');
      if (!mainCarousel) {
        console.log('No shop-the-look carousel found on page');
        return [];
      }
      
      // Get all product cards except the current one
      const productCards = Array.from(mainCarousel.querySelectorAll('[data-product-handle]'))
        .filter(el => el.dataset.productHandle !== product.handle)
        .slice(0, 6);
      
      if (productCards.length === 0) {
        console.log('No related products found');
        return [];
      }
      
      // Extract product data from the existing cards
      const relatedProducts = productCards.map(card => {
        const link = card.closest('a') || card.querySelector('a');
        const img = card.closest('.shop-the-look__item')?.querySelector('.shop-the-look__image');
        const title = card.closest('.shop-the-look__item')?.querySelector('.shop-the-look__product-title');
        const priceEl = card.closest('.shop-the-look__item')?.querySelector('.shop-the-look__product-price');
        
        if (!link || !title) return null;
        
        // Extract price from text (remove currency symbols and parse)
        let price = 0;
        if (priceEl) {
          const priceText = priceEl.textContent.trim();
          const priceMatch = priceText.match(/[\d,]+/);
          if (priceMatch) {
            price = parseFloat(priceMatch[0].replace(/,/g, '')) * 100; // Convert to cents
          }
        }
        
        return {
          url: link.href,
          title: title.textContent.trim(),
          featured_image: img?.src || '',
          price: price
        };
      }).filter(p => p !== null);
      
      console.log('Found related products:', relatedProducts);
      return relatedProducts;
      
    } catch (error) {
      console.log('Could not fetch related products:', error);
      return [];
    }
  }

  renderProductPopup(product, relatedProducts, body, popup) {
    const firstAvailableVariant = product.variants.find(v => v.available) || product.variants[0];
    
    let html = `
      <button class="shop-the-look-popup__close" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    `;
    
    // Show variant options vertically - clicking adds to cart
    html += '<div class="shop-the-look-popup__variants">';
    
    if (product.variants.length > 1) {
      // Multiple variants - list all
      html += '<div class="shop-the-look-popup__variant-group"><div class="shop-the-look-popup__variant-options">';
      
      product.variants.forEach(variant => {
        const variantTitle = variant.title !== 'Default Title' ? variant.title : 'Add to Cart';
        html += `
          <button type="button" 
            class="shop-the-look-popup__variant-option" 
            data-variant-id="${variant.id}"
            data-product-handle="${product.handle}"
            ${!variant.available ? 'disabled' : ''}>
            ${variantTitle}
          </button>
        `;
      });
      
      html += '</div></div>';
    } else {
      // Single variant - show simple add to cart
      html += `
        <div class="shop-the-look-popup__variant-group">
          <div class="shop-the-look-popup__variant-options">
            <button type="button" 
              class="shop-the-look-popup__variant-option" 
              data-variant-id="${firstAvailableVariant.id}"
              data-product-handle="${product.handle}"
              ${!firstAvailableVariant.available ? 'disabled' : ''}>
              Add to Cart
            </button>
          </div>
        </div>
      `;
    }
    
    html += '</div>';
    
    body.innerHTML = html;
    
    // Setup variant click handlers to add directly to cart
    const variantButtons = body.querySelectorAll('.shop-the-look-popup__variant-option');
    variantButtons.forEach((variantButton) => {
      variantButton.addEventListener('click', () => {
        if (variantButton.disabled) return;
        const variantId = variantButton.dataset.variantId;
        this.addToCart(product, variantId, relatedProducts, popup, variantButton);
      });
    });
  }

  async addToCart(product, variantId, relatedProducts, popup, sourceButton) {
    const statusButton =
      sourceButton ||
      popup.querySelector(`.shop-the-look-popup__variant-option[data-variant-id="${variantId}"]`) ||
      popup.querySelector('.shop-the-look-popup__add-button');

    const originalText = statusButton ? statusButton.textContent : null;
    if (statusButton) {
      statusButton.disabled = true;
      statusButton.textContent = 'Adding...';
    }
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });
      
      if (!response.ok) throw new Error('Failed to add to cart');
      
      const item = await response.json();
      
      // Fetch updated cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Refresh cart (if cart drawer exists)
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }
      
      // Publish cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Show confirmation screen
      this.showConfirmation(product, item, cart, relatedProducts, popup);
      
    } catch (error) {
      if (statusButton) {
        statusButton.textContent = 'Error - Try Again';
        statusButton.disabled = false;
      }
      
      setTimeout(() => {
        if (statusButton && originalText != null) {
          statusButton.textContent = originalText;
        }
      }, 2000);
    }
  }

  showConfirmation(product, item, cart, relatedProducts, popup) {
    popup.setAttribute('data-state', 'confirmed');
    this.setPopupContentMode(popup, 'modal');
    const body = popup.querySelector('.shop-the-look-popup__body');
    
    // Get variant details
    const variant = product.variants.find(v => v.id === item.variant_id);
    const variantOptions = variant ? variant.options.filter(opt => opt !== 'Default Title').join(', ') : '';
    
    // Get image URL - try multiple sources
    let imageUrl = item.image || item.featured_image || product.featured_image;
    if (!imageUrl && product.images && product.images.length > 0) {
      imageUrl = product.images[0];
    }
    
    console.log('Related products:', relatedProducts); // Debug
    
    let html = `
      <button class="shop-the-look-popup__close" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
      
      <div class="shop-the-look-confirmation">
        <h2 class="shop-the-look-confirmation__title">ADDED TO BAG</h2>
        
        <div class="shop-the-look-confirmation__product">
          <div class="shop-the-look-confirmation__product-image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" width="120" height="120">` : ''}
          </div>
          <div class="shop-the-look-confirmation__product-details">
            <h3 class="shop-the-look-confirmation__product-title">${product.title}</h3>
            ${variantOptions ? `<p class="shop-the-look-confirmation__product-variant">${variantOptions}</p>` : ''}
            <p class="shop-the-look-confirmation__product-price">${this.formatMoney(item.price)}</p>
          </div>
        </div>
        
        <div class="shop-the-look-confirmation__subtotal">
          <span>Order Subtotal (${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}):</span>
          <strong>${this.formatMoney(cart.total_price)}</strong>
        </div>
        
        <div class="shop-the-look-confirmation__actions">
          <button type="button" class="shop-the-look-confirmation__view-bag">
            VIEW BAG
          </button>
          <button type="button" class="shop-the-look-confirmation__checkout">
            CHECKOUT
          </button>
        </div>
    `;
    
    // Add related products section if available
    if (relatedProducts && relatedProducts.length > 0) {
      html += `
        <div class="shop-the-look-confirmation__related">
          <h3 class="shop-the-look-confirmation__related-title">PAIRS GREAT WITH</h3>
          <div class="shop-the-look-confirmation__related-carousel">
            <button type="button" class="shop-the-look-confirmation__nav shop-the-look-confirmation__nav--prev" aria-label="Previous">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <div class="shop-the-look-confirmation__related-track">
              ${relatedProducts.map(relProduct => `
                <a href="${relProduct.url}" class="shop-the-look-confirmation__related-item">
                  <img src="${relProduct.featured_image}" alt="${relProduct.title}" width="150" height="150" loading="lazy">
                  <h4>${relProduct.title}</h4>
                  <p>${this.formatMoney(relProduct.price)}</p>
                </a>
              `).join('')}
            </div>
            <button type="button" class="shop-the-look-confirmation__nav shop-the-look-confirmation__nav--next" aria-label="Next">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    }
    
    html += `</div>`;
    
    body.innerHTML = html;
    
    // Setup button click handlers
    const viewBagBtn = body.querySelector('.shop-the-look-confirmation__view-bag');
    const checkoutBtn = body.querySelector('.shop-the-look-confirmation__checkout');
    
    if (viewBagBtn) {
      viewBagBtn.addEventListener('click', () => {
        window.location.href = '/cart';
      });
    }
    
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        window.location.href = '/checkout';
      });
    }
    
    // Setup related products carousel if exists
    if (relatedProducts && relatedProducts.length > 0) {
      this.setupRelatedCarousel(body);
    }
  }

  setupRelatedCarousel(container) {
    const track = container.querySelector('.shop-the-look-confirmation__related-track');
    const prevBtn = container.querySelector('.shop-the-look-confirmation__nav--prev');
    const nextBtn = container.querySelector('.shop-the-look-confirmation__nav--next');
    const items = container.querySelectorAll('.shop-the-look-confirmation__related-item');
    
    if (!track || items.length === 0) return;
    
    let currentIndex = 0;
    const itemsPerView = window.innerWidth < 750 ? 2 : 3;
    let maxIndex = Math.max(0, items.length - itemsPerView);
    
    const updateCarousel = () => {
      maxIndex = Math.max(0, items.length - itemsPerView);
      currentIndex = Math.min(currentIndex, maxIndex);

      const itemWidth = items[0].offsetWidth;
      const gap = 16;
      const offset = -(currentIndex * (itemWidth + gap));
      track.style.transform = `translateX(${offset}px)`;
      
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= maxIndex;

      const shouldShowNav = items.length > itemsPerView;
      if (prevBtn) prevBtn.hidden = !shouldShowNav;
      if (nextBtn) nextBtn.hidden = !shouldShowNav;
    };
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        currentIndex = Math.max(0, currentIndex - 1);
        updateCarousel();
      });
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        currentIndex = Math.min(maxIndex, currentIndex + 1);
        updateCarousel();
      });
    }
    
    updateCarousel();
  }

  formatMoney(cents) {
    // Basic money formatting - adjust based on your shop's currency settings
    const money = (cents / 100).toFixed(2);
    return `¥${money.replace(/\.00$/, '')}`;
  }
}

customElements.define('shop-the-look', ShopTheLook);

